import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import HaCoverageRadar from '../dashboard/cards/HaCoverageRadar';
import HaShimmerCard from './components/HaShimmerCard';
import MetricTrendChart from './components/MetricTrendChart';
import { HaStatTile, HaGaugeTile } from './components/HaDashTiles';
import { HaDonut, HaMonthlyBars } from './components/HaDashCharts';
import { useHrAnalyticsApi } from './config';

// Analiz Paneli (2026-08-03, kullanıcı isteği): HA menüsünün EN ÜSTÜNDE, modüle özel görsel
// dashboard. Elde hazır olan snapshot istatistikleri çeşitli formlarda (karo, yarım daire
// gösterge, çizgi, donut, çubuk, radar) sunulur; her kart HaShimmerCard sarmalayıcısıyla
// (kayan-ışık gradient kenarlık) çizilir ve PNG olarak indirilebilir.
//
// Form seçimleri dataviz yöntemine göre: tek sayı → karo/gösterge; zaman → çizgi; parça-bütün
// (2-3 dilim) → donut; işaretli aylık fark → sıfır-eksenli çubuk; katman kapsaması → radar
// (HaCoverageRadar YENİDEN KULLANILIR — aynı bileşen, ana paneldekiyle bire bir).
const TENANT_CODES = [
  'headcount.active',
  'turnover.total.rate',
  'retention.rate.12m',
  'headcount.vacancy.rate',
  'headcount.netChange.monthly',
  'workforce.markov.projection',
];

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 16px;
`;

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
`;

const StateMessage = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

function seriesOf(results, code) {
  const r = (results || []).find((x) => x.code === code);
  if (!r || r.state !== 'available') return [];
  return (r.cells || []).filter((c) => c.value !== null && c.value !== undefined);
}

const last = (cells) => (cells.length > 0 ? cells[cells.length - 1] : null);

export default function HrAnalyticsDashboardPage() {
  const { t, i18n } = useTranslation();
  const { getCatalog, getMetrics } = useHrAnalyticsApi();

  const [catalog, setCatalog] = useState([]);
  const [tenantResults, setTenantResults] = useState([]);
  const [locationCells, setLocationCells] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    // Hata toast'ı YOK (CLAUDE.md) — hatalar yalnız Axios interceptor'da.
    let cancelled = false;
    setFetching(true);
    Promise.all([
      getCatalog().then((r) => { if (!cancelled) setCatalog(r?.data?.data ?? []); }),
      getMetrics({ codes: TENANT_CODES.join(','), scope: 'tenant', grain: 'month' })
        .then((r) => { if (!cancelled) setTenantResults(r?.data?.data ?? []); }),
      getMetrics({ codes: 'headcount.active', scope: 'location', grain: 'month' })
        .then((r) => {
          if (cancelled) return;
          const res = (r?.data?.data ?? []).find((x) => x.code === 'headcount.active');
          setLocationCells(res?.state === 'available' ? (res.cells || []) : []);
        }),
    ]).finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [getCatalog, getMetrics]);

  const pctFmt = useMemo(
    () => new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 1 }),
    [i18n.language]
  );
  const numFmt = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const headcount = seriesOf(tenantResults, 'headcount.active');
  const turnover = seriesOf(tenantResults, 'turnover.total.rate');
  const retention = last(seriesOf(tenantResults, 'retention.rate.12m'));
  const vacancy = last(seriesOf(tenantResults, 'headcount.vacancy.rate'));
  const netChange = seriesOf(tenantResults, 'headcount.netChange.monthly');
  const projection = seriesOf(tenantResults, 'workforce.markov.projection');

  const turnoverLast = last(turnover);
  const turnoverPrev = turnover.length > 1 ? turnover[turnover.length - 2] : null;
  const turnoverDelta = turnoverLast && turnoverPrev
    ? Number(turnoverLast.value) - Number(turnoverPrev.value)
    : null;

  // Donut: en son dönemin lokasyon kırılımı (backend ScopeName gönderiyor).
  const donutData = useMemo(() => {
    const plottable = locationCells.filter((c) => c.value !== null && c.value !== undefined);
    if (plottable.length === 0) return [];
    const latestTime = Math.max(...plottable.map((c) => new Date(c.periodStart).getTime()));
    return plottable
      .filter((c) => new Date(c.periodStart).getTime() === latestTime)
      .sort((a, b) => b.value - a.value)
      .map((c) => ({ label: c.scopeName || c.scopeType, value: Number(c.value) }));
  }, [locationCells]);

  const monthFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { month: 'short' }),
    [i18n.language]
  );
  const barData = useMemo(
    () => netChange.slice(-12).map((c) => ({
      label: monthFmt.format(new Date(c.periodStart)),
      value: Number(c.value),
    })),
    [netChange, monthFmt]
  );

  if (fetching) {
    return <Container><StateMessage>{t('common.loading')}</StateMessage></Container>;
  }

  return (
    <Container>
      <TileGrid>
        {headcount.length > 0 && (
          <HaShimmerCard title={t('hrAnalytics.dashboardMetrics.headcountActive')} fileName="aktif-kadro.png">
            <HaStatTile
              value={numFmt.format(last(headcount).value)}
              sparkValues={headcount.slice(-12).map((c) => Number(c.value))}
            />
          </HaShimmerCard>
        )}
        {turnoverLast && (
          <HaShimmerCard title={t('hrAnalytics.dashboardMetrics.turnoverTotalRate')} fileName="aylik-devir.png">
            <HaStatTile
              value={pctFmt.format(turnoverLast.value)}
              deltaText={turnoverDelta !== null
                ? `${turnoverDelta > 0 ? '+' : ''}${pctFmt.format(turnoverDelta)} ${t('dashboard.haPanel.vsPrevious')}`
                : null}
              deltaTone={turnoverDelta === null || turnoverDelta === 0 ? 'neutral' : turnoverDelta < 0 ? 'good' : 'bad'}
            />
          </HaShimmerCard>
        )}
        {retention && (
          <HaShimmerCard title={t('hrAnalytics.dashboardMetrics.retentionRate12m')} fileName="elde-tutma.png">
            <HaGaugeTile ratio={Number(retention.value)} valueText={pctFmt.format(retention.value)} />
          </HaShimmerCard>
        )}
        {vacancy && (
          <HaShimmerCard title={t('hrAnalytics.dashboardMetrics.headcountVacancyRate')} fileName="acik-pozisyon.png">
            <HaGaugeTile ratio={Number(vacancy.value)} valueText={pctFmt.format(vacancy.value)} />
          </HaShimmerCard>
        )}
      </TileGrid>

      <ChartGrid>
        {turnover.length >= 2 && (
          <HaShimmerCard title={t('hrAnalytics.dashboard.turnoverTrend')} fileName="devir-egilimi.png">
            <MetricTrendChart cells={turnover} formatValue={(v) => pctFmt.format(v)} hideDownload height={230} viewWidth={420} />
          </HaShimmerCard>
        )}
        <HaShimmerCard title={t('dashboard.haPanel.coverageTitle')} fileName="analitik-kapsami.png">
          <HaCoverageRadar catalog={catalog} gradientId="ha-dash-radar-fill" />
        </HaShimmerCard>
        {donutData.length >= 2 && (
          <HaShimmerCard title={t('hrAnalytics.dashboard.locationSplit')} fileName="lokasyon-dagilimi.png">
            <HaDonut data={donutData} />
          </HaShimmerCard>
        )}
        {barData.length >= 3 && (
          <HaShimmerCard title={t('hrAnalytics.dashboard.netChange12m')} fileName="net-degisim.png">
            <HaMonthlyBars data={barData} />
          </HaShimmerCard>
        )}
        {projection.length >= 2 && (
          <HaShimmerCard title={t('hrAnalytics.dashboard.workforceProjection')} fileName="isgucu-projeksiyonu.png">
            <MetricTrendChart cells={projection} formatValue={(v) => numFmt.format(v)} hideDownload height={230} viewWidth={420} />
          </HaShimmerCard>
        )}
      </ChartGrid>
    </Container>
  );
}
